import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SectionCard } from '@/components/common/SectionCard';
import { TextField } from '@/components/forms/TextField';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { clearPurchaseIntent } from '@/lib/auth/intent-storage';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode, getApiErrorDetail, getApiErrorMessage } from '@/lib/utils/error.utils';
import { CONFIRM_STARTUP_STRINGS as S } from '../constants';
import {
  confirmStartupSchema,
  type ConfirmStartupFormValues,
  type ConfirmStartupPayloadValues,
} from '../confirm-startup.schema';
import { useCreateStartup } from '../hooks/useCreateStartup';
import { useStartupEligibility } from '../hooks/useStartupEligibility';
import { EmailReplacementRequiredState } from './EmailReplacementRequiredState';
import { StartupAlreadyExistsState } from './StartupAlreadyExistsState';

/** What the account-level checks allow, resolved once from the eligibility read. */
type Gate = 'loading' | 'work_email' | 'exists' | 'form';

const GATE_BY_CODE: Record<string, Gate> = {
  [API_ERROR_CODES.WORK_EMAIL_REQUIRED]: 'work_email',
  [API_ERROR_CODES.STARTUP_DOMAIN_ALREADY_REGISTERED]: 'exists',
};

/**
 * The Issue Report journey's only startup step (FR-225, MYN-300): company
 * name and website, prefilled from what was typed at signup, plus the
 * account's company email and the Startup Admin role. Confirming creates the
 * startup through the same mutation as Create Startup, so the created page
 * refreshes the session and hands back to the journey, which then opens
 * checkout. The rest of the profile is completed after payment.
 */
export function ConfirmStartupStep() {
  const { user } = useAuth();
  const profile = useProfile();
  const eligibility = useStartupEligibility();
  // Any other eligibility failure falls through to the form: the check is a
  // shortcut, and creating the startup re-checks the same rules server-side.
  const gate: Gate =
    eligibility.isLoading || profile.isLoading
      ? 'loading'
      : (GATE_BY_CODE[getApiErrorCode(eligibility.error) ?? ''] ?? 'form');

  // A startup already holds this domain: the journey ends here, so a later
  // login must not be sent back into it (FR-206).
  useEffect(() => {
    if (gate === 'exists') clearPurchaseIntent();
  }, [gate]);

  const screens: Record<Gate, () => ReactNode> = {
    loading: () => <LoadingSpinner />,
    work_email: () => <EmailReplacementRequiredState inline />,
    exists: () => {
      const company = getApiErrorDetail(eligibility.error);
      const note = company ? S.EXISTS_NOTE.replace('{company}', company) : S.EXISTS_NOTE_FALLBACK;
      return <StartupAlreadyExistsState existingCompanyName={company} note={note} inline />;
    },
    form: () => (
      <ConfirmStartupForm
        email={user?.email ?? ''}
        companyName={profile.data?.signupCompanyName ?? ''}
        companyWebsiteUrl={profile.data?.signupCompanyWebsiteUrl ?? ''}
      />
    ),
  };
  return screens[gate]();
}

interface ConfirmStartupFormProps {
  email: string;
  companyName: string;
  companyWebsiteUrl: string;
}

/** The two editable fields and the read-only identity rows; owns the create request. */
function ConfirmStartupForm({ email, companyName, companyWebsiteUrl }: ConfirmStartupFormProps) {
  const create = useCreateStartup();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmStartupFormValues, unknown, ConfirmStartupPayloadValues>({
    resolver: zodResolver(confirmStartupSchema),
    defaultValues: { companyName, companyWebsiteUrl },
  });

  const onSubmit = handleSubmit((values) => create.mutate({ ...values, workEmail: email }));

  return (
    <SectionCard>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-navy text-[1.625rem] font-bold">{S.TITLE}</h2>
          <p className="text-subtext text-sm">{S.SUBTITLE}</p>
        </div>
        {create.isError && (
          <p role="alert" className="text-danger text-sm">
            {getApiErrorMessage(create.error, S.FAILED)}
          </p>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            {...register('companyName')}
            label={S.NAME_LABEL}
            labelVariant="caps"
            required
            error={errors.companyName?.message}
          />
          <TextField
            {...register('companyWebsiteUrl')}
            label={S.WEBSITE_LABEL}
            labelVariant="caps"
            required
            inputMode="url"
            error={errors.companyWebsiteUrl?.message}
          />
          <div className="flex flex-col gap-0.5">
            <span className="text-subtext text-xs font-semibold tracking-wider uppercase">{S.EMAIL_LABEL}</span>
            <span className="text-navy text-[0.9375rem] font-semibold break-all">{email}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-subtext text-xs font-semibold tracking-wider uppercase">{S.ROLE_LABEL}</span>
            <span className="text-navy text-[0.9375rem] font-semibold">{S.ROLE_VALUE}</span>
          </div>
        </div>
        <p className="bg-green/10 text-dkgreen rounded-lg px-3.5 py-2.5 text-[0.8125rem]">{S.LATER_NOTE}</p>
        <Button type="submit" variant="primary" disabled={create.isPending} className="self-start">
          {create.isPending ? S.SUBMITTING : S.SUBMIT}
        </Button>
      </form>
    </SectionCard>
  );
}
